/**
 * The guard's store reads, in one file — the same split as `kiosk/service/selectors.ts`
 * and `kiosk/catalog/selectors.ts`, and for the same reason: this domain has an imperative
 * adapter (`index.ts`) and a reactive one (`hooks/`), and two adapters naming store keys
 * independently is drift waiting to happen.
 *
 * ⚠️ **`routes.ts` must never import this file.** That module is a pure table with no
 * platform or store dependency, imported by POS-side code on both platforms
 * (`apps/web/src/history.js`, `apps/mobile/src/utils/NavigationUtils.ts`) which reaches it
 * by FILE (`kiosk/navigation/routes`), never by folder. Adding a store read there would
 * pull the whole slice tree into the POS's navigation path and into the documented
 * `apiService → kiosk/config → redux-store` cycle.
 */
// The cart's own definition of empty, not a second one — `ordering/cart` owns what a line
// is, and `isCartEmpty()` in its adapter reads through exactly this function. The rules
// file, not the domain barrel: `ordering/cart/index.ts` is that domain's store adapter and
// pulls half the slice tree in at module eval for what is one array length here.
import { isEmpty } from "../../ordering/cart/lineModel";
import { selectSaleItems } from "../../ordering/cart/selectors";
// The mode flag, from the domain that owns it. `../config/selectors` and not `../config`:
// that barrel sits inside the documented `apiService -> kiosk/config -> redux-store` cycle
// and reads the store itself, where this file only ever takes state as an argument.
import { selectKioskEnabled } from "../config/selectors";
// The placed sale's id, from the domain that owns it — see `selectHasPlacedOrder`.
import { selectSubmittedSaleMid } from "../payment/selectors";
import { KIOSK_ROUTES, MODE_BY_ROUTE } from "./routes";
import type { KioskRoute } from "./routes";

/**
 * The staff session behind the kiosk is dead (§13.10). Written by the app's forced-logout
 * path; survives a session reset (`resetKioskSession` guards it) because only staff can
 * clear it.
 */
export function selectSessionExpired(state: any): boolean {
  return Boolean(state?.kioskStore?.sessionExpired);
}

/** A customer session has been started. `beginSession` is the only writer. */
export function selectHasSession(state: any): boolean {
  return Boolean(state?.kioskStore?.sessionId);
}

/**
 * A service — and therefore a channel — has been chosen.
 *
 * The SESSION's field, not the resolved service: the guard asks whether the customer got
 * past the SERVICE screen, and resolving the id against the branch's channelvos is
 * `kiosk/service`'s job (`useSelectedService` returns null for a service that has since
 * become unservable, which is a different question with a different answer).
 */
export function selectHasService(state: any): boolean {
  return Boolean(state?.kioskStore?.serviceId);
}

/**
 * An order was actually WRITTEN — the only question SUCCESS should be gated on.
 *
 * 🔴 It reads the placed sale's `mid`, not its `orderNumber`. Those are different questions
 * and conflating them was a real bug: "pay at counter" commits a still-OPEN sale, some
 * merchants' backends assign no `orderNumber` until that sale is settled, and the guard then
 * bounced a customer whose order HAD been placed straight back to the attract screen. `mid` is
 * the sale's primary key and is always present. See `selectSubmittedSaleMid`.
 *
 * Delegates to the payment domain's own reader rather than naming the field again:
 * `payment/selectors` takes state as an argument and touches no store, like every other
 * import here.
 */
export function selectHasPlacedOrder(state: any): boolean {
  return Boolean(selectSubmittedSaleMid(state));
}

/** The customer's cart, as the CART screen's own domain defines emptiness. */
export function selectHasCartItems(state: any): boolean {
  return !isEmpty(selectSaleItems(state));
}

/**
 * The route the customer is on, mirrored off the platform router by the navigator wrapper
 * in `packages/ui/kiosk/navigation`. `IDLE` before anything has been written, which is also
 * what `resetKioskSession` restores.
 */
export function selectKioskRoute(state: any): KioskRoute {
  return state?.kioskStore?.route ?? KIOSK_ROUTES.IDLE;
}

/**
 * Is a customer ORDERING on the kiosk right now — browsing the menu or looking at the cart?
 *
 * This is the kiosk's answer to the question `utils/navigation`'s `isNewOrderScreenActive()`
 * answers for the POS: "is a surface that can actually SHOW background work on screen". It is
 * asked from outside the kiosk (`saleUpdate/recommendationEffect`), which is why it lives with
 * the other guard selectors rather than inside a screen.
 *
 * ⚠️ Answered from STATE, not from navigation, and that is deliberate. The POS's version has to
 * be platform-split because each platform keeps "which screen is showing" somewhere different;
 * the kiosk mirrors its route into `kioskStore`, so one selector serves both platforms — and it
 * cannot disagree with the route guard, which reads the same field.
 *
 * The mode flag is part of the answer, not decoration: `route` is a mirror that keeps its last
 * value, and gating on the flag means a kiosk that has been exited can never look "ordering" to
 * a listener that outlives it.
 */
export function selectIsKioskOrderingActive(state: any): boolean {
  return selectKioskEnabled(state) && MODE_BY_ROUTE[selectKioskRoute(state)] === "ordering";
}
